// Agrupado de la conversación al estilo Telegram (S10). Función PURA sobre la línea de tiempo:
// - Separador de día cuando cambia la fecha (local).
// - Las burbujas seguidas del mismo remitente, a menos de 5 minutos, forman un grupo: solo la última
//   lleva cola (y, si es del agente, su avatar); la separación dentro del grupo es menor.
// - Un aviso (hito) o una línea de trabajo corta el grupo, como los mensajes de servicio en Telegram.
// - Los avisos informativos seguidos (contratado, fase, trabajo hecho, plan propuesto…) se funden en UNA línea
//   de servicio (crítica A10: eran cuatro píldoras apiladas). Lo que pide algo a Pau o avisa de un fallo va
//   siempre aparte.
import type { Elemento, Hito } from './linea-de-tiempo';

export type Fila =
  | { tipo: 'dia'; clave: string; fecha: string }
  | { tipo: 'elemento'; clave: string; e: Elemento; primero: boolean; ultimo: boolean }
  | { tipo: 'servicio'; clave: string; elementos: Elemento[] };

const INFORMATIVOS = new Set<Hito['clase']>(['contratado', 'fase', 'pensó', 'informe', 'cierre_pedido', 'contrato_aprobado', 'contrato_propuesto', 'respondiste', 'cambios_pedidos', 'aviso_oficina', 'sin_cupo', 'trasladado', 'permiso_respondido']);
/**
 * Lo que Pau no necesita ver (28-09: «es ruido, no me deja concentrarme en lo importante»): los contadores de
 * trabajo («revisó cosas (4)»), cuánto pensó y los cambios de fase (la cabecera del chat ya dice qué hace).
 * Siguen en el historial de la información; en la conversación solo quedan los hitos que le importan.
 */
const RUIDO = new Set<Hito['clase']>(['fase', 'pensó', 'consulta_github', 'consulta_agentes']);
export const esRuido = (e: Elemento) => e.tipo === 'trabajo' || (e.tipo === 'hito' && RUIDO.has(e.hito.clase));
// 29-09: un permiso que la oficina no pudo cumplir es un fallo: va aparte, no fundido en la línea gris.
export const esInformativo = (e: Elemento) =>
  e.tipo === 'trabajo' || (e.tipo === 'hito' && INFORMATIVOS.has(e.hito.clase) && !(e.hito.clase === 'permiso_respondido' && e.hito.resultado === 'no_se_pudo'));

const JUNTOS_MS = 5 * 60_000;

function tsDe(e: Elemento): string | null {
  return e.tipo === 'trabajo' ? null : e.ts;
}

/** YYYY-MM-DD en la hora local del navegador (el día que ve Pau). */
export function diaLocal(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const esBurbuja = (e: Elemento) => e.tipo === 'pau' || e.tipo === 'agente';

/**
 * Quita el ruido sin perder sus mensajes (29-09): el historial de la información lleva a ellos («ir al
 * mensaje») y, si desaparecían con el elemento oculto, el clic no hacía nada. Sus ids se pliegan en el
 * elemento visible anterior (o en el siguiente, si el ruido va al principio). Copia: no toca la entrada.
 */
export function sinRuido(todos: Elemento[]): Elemento[] {
  const out: Elemento[] = [];
  let sueltos: string[] = [];
  for (const e of todos) {
    if (esRuido(e)) {
      const previo = out[out.length - 1];
      if (previo) previo.ids.push(...e.ids);
      else sueltos.push(...e.ids);
      continue;
    }
    out.push({ ...e, ids: [...e.ids, ...sueltos] });
    sueltos = [];
  }
  return out;
}

export function agrupar(todos: Elemento[]): Fila[] {
  const elementos = sinRuido(todos);
  const out: Fila[] = [];
  let dia = '';
  for (let i = 0; i < elementos.length; i++) {
    const e = elementos[i]!;
    const ts = tsDe(e);
    const d = ts ? diaLocal(ts) : '';
    if (d && d !== dia) {
      out.push({ tipo: 'dia', clave: `dia-${d}`, fecha: ts! });
      dia = d;
    }
    const anterior = elementos[i - 1];
    const siguiente = elementos[i + 1];
    const junto = (a: Elemento | undefined, b: Elemento | undefined): boolean => {
      if (!a || !b || !esBurbuja(a) || !esBurbuja(b) || a.tipo !== b.tipo) return false;
      const ta = tsDe(a);
      const tb = tsDe(b);
      if (!ta || !tb || diaLocal(ta) !== diaLocal(tb)) return false;
      return Math.abs(Date.parse(tb) - Date.parse(ta)) <= JUNTOS_MS;
    };
    if (esInformativo(e)) {
      const previa = out[out.length - 1];
      if (previa?.tipo === 'servicio') previa.elementos.push(e);
      else out.push({ tipo: 'servicio', clave: `s-${e.id}`, elementos: [e] });
      continue;
    }
    out.push({ tipo: 'elemento', clave: e.id, e, primero: !junto(anterior, e), ultimo: !junto(e, siguiente) });
  }
  return out;
}
